import type { MiniAppApi } from './api';
import type {
    BalanceHistory,
    Account,
    ClubLoyalty,
    OnlinePaymentView,
    LoyaltyCriterion,
    ReferralOverview,
    Target,
    TelegramUser,
} from './types';

export const previewViews = [
    { value: 'dashboard', label: 'Головна' },
    { value: 'empty', label: 'Без клубів' },
    { value: 'fallbacks', label: 'Без зображень' },
    { value: 'contact', label: 'Телефон' },
    { value: 'register', label: 'Реєстрація' },
    { value: 'set-password', label: 'Перенесений · пароль' },
    { value: 'join', label: 'Вступ до клубу' },
    {
        value: 'join-invite-existing',
        label: 'Вступ · запрошення, вже є акаунт',
    },
    {
        value: 'joined-invite-existing',
        label: 'Вступив · запрошення, вже є акаунт',
    },
    { value: 'session', label: 'Сесія за ПК' },
    { value: 'session-blocked', label: 'Сесія · заблокований' },
    { value: 'session-reserved', label: 'Сесія · заброньований ПК' },
    { value: 'profile', label: 'Профіль' },
    { value: 'invite', label: 'Запросити друга' },
    { value: 'invite-new', label: 'Запросити друга · нікого' },
    { value: 'invite-levels', label: 'Запросити друга · 2 рівні' },
    { value: 'loyalty', label: 'Квести й досягнення' },
    { value: 'loyalty-empty', label: 'Квести · порожньо' },
    { value: 'loyalty-loading', label: 'Квести · завантаження' },
    { value: 'history', label: 'Історія операцій' },
    { value: 'history-empty', label: 'Історія операцій · порожньо' },
    { value: 'history-loading', label: 'Історія операцій · завантаження' },
    { value: 'topup', label: 'Поповнити онлайн' },
    { value: 'topup-paid', label: 'Поповнити онлайн · оплачено' },
    { value: 'topup-failed', label: 'Поповнити онлайн · відмова' },
    { value: 'password', label: 'Скидання пароля' },
    { value: 'loading', label: 'Завантаження' },
] as const;

export type PreviewView = (typeof previewViews)[number]['value'];

type PreviewData = {
    telegramUser: TelegramUser;
    account: Account;
    registrationTarget: Target;
    joinTarget: Target;
    computerTarget: Target;
    blockedComputerTarget: Target;
    reservedComputerTarget: Target;
};

export function createPreviewData(): PreviewData {
    const avatar = '/preview/user-avatar.png';
    const networkLogo = '/preview/club-nitrogg.svg';
    const downtownLogo = '/preview/club-loud.svg';
    const obolonLogo = '/preview/club-esl.svg';

    return {
        telegramUser: {
            id: 987654321,
            first_name: 'Олексій',
            last_name: 'Мельник',
            username: 'alex_player',
            photo_url: avatar,
        },
        account: {
            profile: {
                id: 42,
                first_name: 'Олексій',
                last_name: 'Мельник',
                nickname: 'alex_player',
                email: 'alex@example.com',
                phone: '+380 67 123 45 67',
                dob: '1998-06-15',
                locale: null,
                theme: null,
                avatar_url: avatar,
                avatar_source: 'telegram',
                has_password: true,
            },
            networks: [
                {
                    id: 1,
                    name: 'CyberZone',
                    logo_url: networkLogo,
                    deposit: 2450.5,
                    online_topup: true,
                    status: 'active',
                    loyalty: { xp: 2840, level: 'Diamond' },
                    clubs: [
                        {
                            id: 11,
                            name: 'CyberZone Downtown',
                            address: 'вул. Велика Васильківська, 72',
                            logo_url: downtownLogo,
                            bonus: 520,
                            blocked: false,
                            quests: true,
                        },
                        {
                            id: 12,
                            name: 'CyberZone Obolon',
                            address: 'Оболонська набережна, 9',
                            logo_url: obolonLogo,
                            bonus: 185,
                            blocked: false,
                            quests: true,
                        },
                    ],
                },
                {
                    id: 2,
                    name: 'Arena One',
                    logo_url: null,
                    deposit: 780,
                    online_topup: true,
                    status: 'active',
                    // Its owner switched the referral programme off: no «Запросити друга».
                    referral_enabled: false,
                    loyalty: { xp: 690, level: 'Silver' },
                    clubs: [
                        {
                            id: 13,
                            name: 'Arena One',
                            address: 'вул. Антоновича, 50',
                            logo_url: null,
                            bonus: 90,
                            blocked: false,
                        },
                        {
                            id: 14,
                            name: 'Arena One Podil',
                            address: 'вул. Нижній Вал, 15',
                            logo_url: null,
                            bonus: 0,
                            // One blocked club in the fixture, so the state can be seen.
                            blocked: true,
                        },
                    ],
                },
                {
                    id: 3,
                    name: 'Purple Grid',
                    logo_url: null,
                    deposit: 320,
                    online_topup: true,
                    status: 'active',
                    loyalty: { xp: 120, level: 'Bronze' },
                    clubs: [
                        {
                            id: 15,
                            name: 'Purple Grid Center',
                            address: 'вул. Хрещатик, 24',
                            logo_url: null,
                            bonus: 0,
                            blocked: false,
                        },
                        {
                            id: 16,
                            name: 'Purple Grid North',
                            address: 'просп. Оболонський, 18',
                            logo_url: null,
                            bonus: 0,
                            blocked: false,
                        },
                    ],
                },
            ],
        },
        registrationTarget: {
            kind: 'registration',
            payload: 'reg_preview',
            club: {
                id: 11,
                name: 'CyberZone Downtown',
                logo_url: downtownLogo,
                network_id: 1,
                network_name: 'CyberZone',
            },
            computer: null,
        },
        joinTarget: {
            kind: 'registration',
            payload: 'reg_join_preview',
            club: {
                id: 21,
                name: 'Vector Gaming Club',
                logo_url: networkLogo,
                network_id: 3,
                network_name: 'Vector',
            },
            computer: null,
        },
        computerTarget: {
            kind: 'computer',
            payload: 'pc_preview',
            club: {
                id: 11,
                name: 'CyberZone Downtown',
                logo_url: downtownLogo,
                network_id: 1,
                network_name: 'CyberZone',
            },
            computer: { id: 108, name: 'VIP-08', zone: 'VIP Lounge' },
        },
        // The same PC, scanned by somebody this club has blocked: no «Почати сесію» to
        // press, just the reason and a way out.
        blockedComputerTarget: {
            kind: 'computer',
            payload: 'pc_preview_blocked',
            club: {
                id: 11,
                name: 'CyberZone Downtown',
                logo_url: downtownLogo,
                network_id: 1,
                network_name: 'CyberZone',
            },
            computer: { id: 108, name: 'VIP-08', zone: 'VIP Lounge' },
            blocked: true,
        },
        // A seat somebody else has booked. Nothing marks it before the fact — the club
        // knows, the PC does not — so the sheet looks exactly like a free machine and the
        // refusal arrives when «Почати сесію» is pressed. The preview reproduces that,
        // including the server's own wording.
        reservedComputerTarget: {
            kind: 'computer',
            payload: 'pc_preview_reserved',
            club: {
                id: 11,
                name: 'CyberZone Downtown',
                logo_url: downtownLogo,
                network_id: 1,
                network_name: 'CyberZone',
            },
            computer: { id: 104, name: 'STD-04', zone: 'Standard' },
        },
    };
}

export type PreviewInvite = 'invite' | 'invite-new' | 'invite-levels';

/**
 * «Запросити друга» as the stand shows it, on the CyberZone network screen. `invite` is a
 * one-level programme with every state a friend can be in — a reward on the balance, one
 * still waiting out the cooldown, one cancelled by a refund, a friend who has not paid yet.
 * `invite-new` is the first visit: nobody invited. `invite-levels` is a two-level programme,
 * the only case with «Друзі ваших друзів».
 */
export function createPreviewReferrals(kind: PreviewInvite): ReferralOverview {
    const link = 'https://t.me/highlink_bot?startapp=ref_K7Q2M9XA';
    const program = {
        enabled: true,
        network: 'CyberZone',
        levels: kind === 'invite-levels' ? 2 : 1,
        rates: kind === 'invite-levels' ? [10, 3] : [10],
        min_payment: 200,
        cooldown_days: 7,
    };

    if (kind === 'invite-new') {
        return {
            network_id: 1,
            code: 'K7Q2M9XA',
            link,
            program,
            summary: { invited: 0, bought: 0, earned: 0, pending: 0 },
            friends: [],
            indirect: [],
        };
    }

    const day = (offset: number) =>
        new Date(Date.now() + offset * 86_400_000).toISOString();
    const reward = (
        status: ReferralOverview['friends'][number]['rewards'][number]['status'],
        amount: number,
        trigger: 'top_up' | 'purchase',
        triggerAmount: number,
        level = 1,
    ) => ({
        level,
        status,
        amount,
        trigger,
        trigger_amount: triggerAmount,
        club: 'CyberZone Downtown',
        at: day(-3),
        credit_at: day(4),
    });

    return {
        network_id: 1,
        code: 'K7Q2M9XA',
        link,
        program,
        summary: { invited: 4, bought: 2, earned: 80, pending: 50 },
        friends: [
            {
                id: 1,
                name: 'Олена К.',
                registered_at: day(-20),
                rewards: [reward('converted', 80, 'top_up', 800)],
            },
            {
                id: 2,
                name: 'Максим Б.',
                registered_at: day(-5),
                rewards: [reward('pending', 50, 'purchase', 500)],
            },
            {
                id: 3,
                name: '@nightowl',
                registered_at: day(-9),
                rewards: [reward('cancelled', 30, 'top_up', 300)],
            },
            { id: 4, name: 'Ірина С.', registered_at: day(-1), rewards: [] },
        ],
        indirect:
            kind === 'invite-levels'
                ? [
                      {
                          id: 5,
                          name: 'Дмитро Л.',
                          rewards: [reward('converted', 12, 'top_up', 400, 2)],
                      },
                  ]
                : [],
    };
}

export type PreviewLoyalty = 'loyalty' | 'loyalty-empty' | 'loyalty-loading';

function criterion(
    metric: string,
    qty: number,
    current: number,
    extra: Partial<LoyaltyCriterion> = {},
): LoyaltyCriterion {
    return {
        metric,
        qty,
        current,
        product: null,
        zone: null,
        days: [],
        from: null,
        to: null,
        ...extra,
    };
}

/**
 * «Квести й досягнення» as the stand shows it. `loyalty` has every state the board knows: a
 * quest under way, one done, one starting later today, an event multiplier in force, and the
 * four rarities of achievement — done, half way, repeatable. `loyalty-empty` is a club with
 * the board on but nothing set up yet. The second club (Obolon) is a lower level, so the
 * chips on top show the switch.
 */
export function createPreviewLoyalty(
    kind: PreviewLoyalty,
    clubId: number,
): ClubLoyalty {
    const obolon = clubId === 12;
    const club = obolon
        ? {
              id: 12,
              name: 'CyberZone Obolon',
              logo_url: '/preview/club-esl.svg',
          }
        : {
              id: 11,
              name: 'CyberZone Downtown',
              logo_url: '/preview/club-loud.svg',
          };
    const today = new Date().toISOString().slice(0, 10);
    const at = (time: string) => `${today}T${time}:00+03:00`;

    const empty = kind === 'loyalty-empty';

    return {
        club,
        loyalty: {
            currency: 'XP',
            lifetime_xp: obolon ? 640 : 2840,
            balance_xp: obolon ? 120 : 1260,
            level: obolon
                ? {
                      name: 'Silver',
                      color: '#94a3b8',
                      icon: '🥈',
                      image_url: null,
                  }
                : {
                      name: 'Diamond',
                      color: '#38bdf8',
                      icon: '💎',
                      image_url: null,
                  },
            next_level: obolon
                ? { name: 'Gold', threshold: 1000, xp_remaining: 360 }
                : { name: 'Legend', threshold: 4000, xp_remaining: 1160 },
            progress_pct: obolon ? 64 : 71,
            board: {
                currency: 'XP',
                multipliers:
                    empty || obolon
                        ? []
                        : [
                              {
                                  id: 1,
                                  icon: '🔥',
                                  name: 'Вечірній буст',
                                  mode: 'factor',
                                  value: 2,
                                  zone: 'VIP',
                                  until: '23:00',
                              },
                          ],
                quests: empty
                    ? []
                    : [
                          {
                              id: 1,
                              icon: '🎮',
                              name: 'Вечірній марафон',
                              description: null,
                              xp: 150,
                              state: 'active',
                              time_from: '18:00',
                              time_to: '23:00',
                              starts_at: at('18:00'),
                              ends_at: at('23:00'),
                              progress_pct: 60,
                              criteria: [
                                  criterion('hours', 3, 1.8, { zone: 'VIP' }),
                              ],
                          },
                          {
                              id: 2,
                              icon: '🥤',
                              name: 'Енергія для перемоги',
                              description: 'Будь-який енергетик з бару.',
                              xp: 40,
                              state: 'active',
                              time_from: null,
                              time_to: null,
                              starts_at: at('00:00'),
                              ends_at: at('23:59'),
                              progress_pct: 50,
                              criteria: [
                                  criterion('product', 2, 1, {
                                      product: 'Monster Energy',
                                  }),
                              ],
                          },
                          {
                              id: 3,
                              icon: '☀️',
                              name: 'Ранкова розминка',
                              description: null,
                              xp: 60,
                              state: 'done',
                              time_from: '09:00',
                              time_to: '13:00',
                              starts_at: at('09:00'),
                              ends_at: at('13:00'),
                              progress_pct: 100,
                              criteria: [criterion('hours', 1, 1)],
                          },
                          {
                              id: 4,
                              icon: '🌙',
                              name: 'Нічна зміна',
                              description: null,
                              xp: 200,
                              state: 'upcoming',
                              time_from: '23:30',
                              time_to: '23:59',
                              starts_at: at('23:30'),
                              ends_at: at('23:59'),
                              progress_pct: 0,
                              criteria: [criterion('spend', 300, 0)],
                          },
                      ],
                achievements: empty
                    ? []
                    : [
                          {
                              id: 1,
                              icon: '👑',
                              name: 'Легенда клубу',
                              description: null,
                              rarity: 'legendary',
                              xp: 1000,
                              is_repeatable: false,
                              times_done: 1,
                              state: 'done',
                              progress_pct: 100,
                              criteria: [criterion('hours', 100, 100)],
                          },
                          {
                              id: 2,
                              icon: '🔁',
                              name: 'Постійний гість',
                              description: null,
                              rarity: 'rare',
                              xp: 80,
                              is_repeatable: true,
                              times_done: 3,
                              state: 'progress',
                              progress_pct: 40,
                              criteria: [criterion('visits', 5, 2)],
                          },
                          {
                              id: 3,
                              icon: '🧠',
                              name: 'Стратег',
                              description: null,
                              rarity: 'epic',
                              xp: 400,
                              is_repeatable: false,
                              times_done: 0,
                              state: 'progress',
                              progress_pct: 72,
                              criteria: [
                                  criterion('hours', 25, 18, { days: [6, 0] }),
                              ],
                          },
                          {
                              id: 4,
                              icon: '🍕',
                              name: 'Гурман',
                              description: null,
                              rarity: 'common',
                              xp: 30,
                              is_repeatable: false,
                              times_done: 1,
                              state: 'done',
                              progress_pct: 100,
                              criteria: [criterion('spend', 500, 500)],
                          },
                          {
                              id: 5,
                              icon: '🌃',
                              name: 'Нічна сова',
                              description: null,
                              rarity: 'rare',
                              xp: 150,
                              is_repeatable: false,
                              times_done: 0,
                              state: 'progress',
                              progress_pct: 20,
                              criteria: [
                                  criterion('visits', 5, 1, {
                                      from: '00:00',
                                      to: '06:00',
                                  }),
                              ],
                          },
                          {
                              id: 6,
                              icon: '🐉',
                              name: 'Повелитель VIP',
                              description: null,
                              rarity: 'legendary',
                              xp: 800,
                              is_repeatable: false,
                              times_done: 0,
                              state: 'progress',
                              progress_pct: 8,
                              criteria: [
                                  criterion('hours', 50, 4, { zone: 'VIP' }),
                              ],
                          },
                      ],
            },
        },
    };
}

export type PreviewTopUp = 'topup' | 'topup-paid' | 'topup-failed';

/**
 * «Поповнити онлайн» in the browser preview (2026-10-27): the sheet's three calls answered here,
 * no monobank. Two clubs, the second with its shift closed; «Оплатити» waits two polls and ends
 * paid, or refused for `topup-failed`.
 */
export function createPreviewTopUpApi(
    kind: PreviewTopUp,
    networkId: number,
    clubs: { id: number; name: string }[],
    balance: number,
): Pick<MiniAppApi, 'topUpOptions' | 'startTopUp' | 'topUpStatus'> {
    const amounts = {
        min: 20,
        presets: [50, 100, 200, 500],
        custom: true,
    };
    let polls = 0;
    let started: OnlinePaymentView | null = null;

    return {
        topUpOptions: async () => ({
            network_id: networkId,
            balance,
            default_club_id: clubs[0]?.id ?? null,
            clubs: clubs.map((club, index) => ({
                id: club.id,
                name: club.name,
                available: index === 0,
                reason:
                    index === 0
                        ? null
                        : {
                              code: 'shift_closed',
                              message:
                                  'Зміна клубу закрита, зверніться до адміністратора.',
                          },
                amounts,
                provider: 'mono',
                provider_label: 'plata by mono',
            })),
        }),
        startTopUp: async (clubId, amount) => {
            polls = 0;
            started = {
                id: 'preview-payment',
                status: 'pending',
                amount,
                provider: 'mono',
                provider_label: 'plata by mono',
                page_url: null,
                expires_at: new Date(Date.now() + 600_000).toISOString(),
                failure_reason: null,
                club: clubs.find((c) => c.id === clubId) ?? null,
                balance,
                poll_interval: 2,
            };

            return started;
        },
        topUpStatus: async () => {
            polls += 1;

            if (!started || polls < 2 || kind === 'topup') {
                return started!;
            }

            return kind === 'topup-paid'
                ? {
                      ...started,
                      status: 'paid',
                      balance: balance + started.amount,
                  }
                : {
                      ...started,
                      status: 'failed',
                      failure_reason: 'Недостатньо коштів на картці',
                  };
        },
    };
}

/**
 * «Історія операцій» as the stand shows it (2026-10-28): every kind of line, over a few days, so
 * the day headings, the colours and a session still running are all on screen.
 */
export type PreviewHistory = 'history' | 'history-empty';

export function createPreviewBalanceHistory(
    kind: PreviewHistory = 'history',
): BalanceHistory {
    if (kind === 'history-empty') {
        return {
            balance: 0,
            totals: { in: 0, out: 0 },
            rows: [],
            next_cursor: null,
        };
    }

    const at = (days: number, hour: number, minute = 0) => {
        const date = new Date();
        date.setDate(date.getDate() - days);
        date.setHours(hour, minute, 0, 0);

        return date.toISOString();
    };
    const row = (
        id: string,
        date: string,
        kind: BalanceHistory['rows'][number]['kind'],
        amount: number,
        balanceAfter: number,
        extra: Partial<BalanceHistory['rows'][number]> = {},
    ): BalanceHistory['rows'][number] => ({
        id,
        date,
        kind,
        effect:
            kind === 'purchase' || kind === 'topup_refund' || kind === 'session'
                ? 'out'
                : 'in',
        amount,
        balance_after: balanceAfter,
        method: kind === 'session' ? 'balance' : null,
        club: 'CyberZone Downtown',
        receipt_code: null,
        description: null,
        computer: null,
        minutes: null,
        running: false,
        ...extra,
    });

    return {
        balance: 412.5,
        totals: { in: 7480, out: 7067.5 },
        next_cursor: 'preview-1',
        rows: [
            row('sess-9', at(0, 18, 40), 'session', 37.5, 412.5, {
                computer: 'VIP-03',
                minutes: 45,
                running: true,
            }),
            row('cm-8', at(0, 17, 55), 'topup', 300, 450, {
                method: 'online',
                receipt_code: 'AE6EZ',
            }),
            row('cm-7', at(1, 22, 10), 'purchase', 95, 150, {
                method: 'balance',
                description: '2× Coca-Cola 0.5, Snickers',
                receipt_code: 'K7Q2M',
            }),
            row('sess-6', at(1, 21, 58), 'session', 120, 245, {
                computer: 'STD-04',
                minutes: 180,
            }),
            row('cm-5', at(1, 18, 30), 'topup', 200, 365, {
                method: 'cash',
                receipt_code: 'HNF8K',
                club: 'CyberZone Saltivka',
            }),
            row('cm-4', at(4, 15, 0), 'refund', 35, 165, {
                method: 'balance',
                description: 'Red Bull 0.25',
            }),
            row('sess-3', at(5, 20, 0), 'session', 80, 130, {
                computer: 'STD-11',
                minutes: 120,
            }),
            row('cm-2', at(5, 19, 50), 'topup', 200, 210, {
                method: 'card',
            }),
        ],
    };
}

/**
 * An older page of the preview history (2026-10-29), so scrolling to the bottom of the demo
 * loads more the way the live one does: two more pages of 50, games and top-ups a few hours
 * apart, the balance running on from the first page's last row (10 ₴ before it).
 */
export function createPreviewBalanceHistoryPage(
    cursor: string,
): BalanceHistory {
    const page = Number(cursor.replace('preview-', '')) || 1;
    const rows: BalanceHistory['rows'] = [];
    // The first page ends 5 days back at 19:50 with 10 ₴ before it.
    let balance = 10;
    let date = new Date();
    date.setDate(date.getDate() - 5);
    date.setHours(19, 50, 0, 0);

    // Walk back through the pages before this one too, so every page starts where one ended.
    for (let i = 0; i < page * 50; i++) {
        date = new Date(date.getTime() - (3 + (i % 4) * 5) * 3_600_000);
        // Read backwards a top-up takes money away, so it needs that much on the balance.
        const topUp = balance >= 150;
        const amount = topUp
            ? balance >= 300
                ? 300
                : 100
            : [40, 60, 85][i % 3];
        const index = i - (page - 1) * 50;

        if (index >= 0) {
            rows.push({
                id: `${topUp ? 'cm' : 'sess'}-p${page}-${index}`,
                date: date.toISOString(),
                kind: topUp ? 'topup' : 'session',
                effect: topUp ? 'in' : 'out',
                amount,
                balance_after: balance,
                method: topUp
                    ? (['cash', 'card', 'online'][i % 3] ?? null)
                    : 'balance',
                club: 'CyberZone Downtown',
                receipt_code: null,
                description: null,
                computer: topUp ? null : `STD-0${(i % 8) + 1}`,
                minutes: topUp ? null : amount * 1.5,
                running: false,
            });
        }

        balance = topUp ? balance - amount : balance + amount;
    }

    return {
        balance: 412.5,
        totals: { in: 7480, out: 7067.5 },
        rows,
        next_cursor: page < 2 ? `preview-${page + 1}` : null,
    };
}
